import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2, CalendarDays, Clock, ShieldCheck, BadgeCheck, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import { MagneticButton } from "../MagneticButton";
import { Calendar } from "../ui/calendar";
import { CONTACT, SERVICES } from "../../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD = "w-full rounded-xl border border-ink-soft bg-ink px-4 py-3 text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold";
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const emptyForm = { name: "", email: "", phone: "", vehicle: "", service: "", address: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!date) return;
    setSlot("");
    setLoadingSlots(true);
    axios
      .get(`${API}/availability`, { params: { date: ymd(date) } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const reset = () => { setForm(emptyForm); setDate(null); setSlots([]); setSlot(""); setDone(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (!date || !slot) {
      toast.error("Please pick a date and an arrival window.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, booking_date: ymd(date), slot });
      setDone(true);
      toast.success("Booking request sent! We'll be in touch to confirm.");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That window was just booked — please pick another.");
        // refresh availability
        axios.get(`${API}/availability`, { params: { date: ymd(date) } }).then((r) => setSlots(r.data.slots));
        setSlot("");
      } else {
        toast.error("Something went wrong. Please call us at " + CONTACT.phone);
      }
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-ink-surface py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal><span className="text-xs font-bold uppercase tracking-[0.3em] text-gold">Get Started</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Book your<br /> <span className="text-gradient-gold">mobile oil change.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-white/55">
                Choose a day and arrival window that works for you. We'll confirm your appointment
                and come straight to you in {CONTACT.area}.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 space-y-4">
                <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4 rounded-2xl border border-ink-soft bg-ink p-4 transition-colors hover:border-gold/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold"><Phone className="h-5 w-5" strokeWidth={1.5} /></span>
                  <div><div className="text-xs text-white/45">Call us</div><div className="font-medium text-white group-hover:text-gold">{CONTACT.phone}</div></div>
                </a>
                <a href={CONTACT.emailHref} data-testid="contact-email" className="group flex items-center gap-4 rounded-2xl border border-ink-soft bg-ink p-4 transition-colors hover:border-gold/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold"><Mail className="h-5 w-5" strokeWidth={1.5} /></span>
                  <div><div className="text-xs text-white/45">Email us</div><div className="font-medium text-white group-hover:text-gold">{CONTACT.email}</div></div>
                </a>
                <div className="flex items-center gap-4 rounded-2xl border border-ink-soft bg-ink p-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold"><MapPin className="h-5 w-5" strokeWidth={1.5} /></span>
                  <div><div className="text-xs text-white/45">Service area</div><div className="font-medium text-white">{CONTACT.area}</div></div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="mt-6 h-56 overflow-hidden rounded-2xl border border-ink-soft grayscale-[0.3]">
                <iframe
                  title="Luigi Lube service area — Lethbridge, Alberta"
                  data-testid="contact-map"
                  src="https://maps.google.com/maps?q=Lethbridge,Alberta&t=&z=11&ie=UTF8&iwloc=&output=embed"
                  className="h-full w-full"
                  style={{ filter: "invert(0.9) hue-rotate(180deg)" }}
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="rounded-3xl border border-ink-soft bg-ink p-8 md:p-10">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  className="flex h-full min-h-[400px] flex-col items-center justify-center text-center"
                  data-testid="booking-success"
                >
                  <CheckCircle2 className="h-16 w-16 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-3xl font-medium">Booking received!</h3>
                  <p className="mt-3 max-w-xs text-white/55">
                    Thanks {form.name.split(" ")[0]} — we've got you down for{" "}
                    <span className="text-gold">{date && date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</span>,{" "}
                    {slots.find((s) => s.id === slot)?.label}. We'll reach out shortly to confirm.
                  </p>
                  <button data-testid="booking-another" onClick={reset} className="mt-8 text-sm text-gold hover:underline">
                    Book another →
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="booking-form" className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input data-testid="booking-name" className={FIELD} placeholder="Full name *" value={form.name} onChange={set("name")} />
                    <input data-testid="booking-phone" className={FIELD} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                  </div>
                  <input data-testid="booking-email" type="email" className={FIELD} placeholder="Email *" value={form.email} onChange={set("email")} />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input data-testid="booking-vehicle" className={FIELD} placeholder="Vehicle (make / model / year)" value={form.vehicle} onChange={set("vehicle")} />
                    <select data-testid="booking-service" className={FIELD} value={form.service} onChange={set("service")}>
                      <option value="">Select service</option>
                      {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                    </select>
                  </div>
                  <input data-testid="booking-address" className={FIELD} placeholder="Service address" value={form.address} onChange={set("address")} />

                  {/* Appointment picker */}
                  <div className="rounded-2xl border border-ink-soft bg-ink-surface p-4" data-testid="appointment-picker">
                    <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white/80">
                      <CalendarDays className="h-4 w-4 text-gold" strokeWidth={1.5} /> Choose a date *
                    </div>
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={{ before: today }}
                        data-testid="booking-calendar"
                        classNames={{
                          caption_label: "text-sm font-medium text-white",
                          nav_button: "h-7 w-7 rounded-md border border-ink-soft bg-ink p-0 text-white/70 hover:text-gold hover:border-gold/40",
                          head_cell: "text-white/40 rounded-md w-8 font-normal text-[0.8rem]",
                          day: "h-8 w-8 p-0 font-normal rounded-md text-white/85 hover:bg-gold/15 hover:text-gold aria-selected:opacity-100",
                          day_selected: "bg-gold text-ink hover:bg-gold hover:text-ink focus:bg-gold focus:text-ink",
                          day_today: "bg-gold/15 text-gold",
                          day_disabled: "text-white/30 opacity-100",
                          day_outside: "text-white/15",
                        }}
                      />
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-white/80">
                      <Clock className="h-4 w-4 text-gold" strokeWidth={1.5} /> Arrival window *
                    </div>
                    {!date ? (
                      <p className="mt-2 text-sm text-white/40">Select a date to see available windows.</p>
                    ) : loadingSlots ? (
                      <p className="mt-2 flex items-center gap-2 text-sm text-white/40"><Loader2 className="h-4 w-4 animate-spin" /> Checking availability…</p>
                    ) : (
                      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="slot-grid">
                        {slots.map((s) => (
                          <button
                            type="button"
                            key={s.id}
                            disabled={!s.available}
                            data-testid={`slot-${s.id}`}
                            onClick={() => setSlot(s.id)}
                            className={`rounded-xl border px-2 py-2.5 text-xs font-medium transition-all ${
                              slot === s.id
                                ? "border-gold bg-gold text-ink"
                                : s.available
                                ? "border-ink-soft bg-ink text-white/80 hover:border-gold/50 hover:text-gold"
                                : "cursor-not-allowed border-ink-soft bg-ink text-white/25 line-through"
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <textarea data-testid="booking-message" rows={3} className={FIELD + " resize-none"} placeholder="Anything else we should know?" value={form.message} onChange={set("message")} />
                  <MagneticButton
                    as="button" type="submit" disabled={loading}
                    data-testid="booking-form-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-semibold text-ink transition-colors hover:bg-gold-hover disabled:opacity-60"
                  >
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Request Booking <Send className="h-4 w-4" strokeWidth={2} /></>}
                  </MagneticButton>
                  <div data-testid="trust-badges" className="flex flex-wrap items-center justify-center gap-2 pt-1">
                    {[
                      { icon: ShieldCheck, label: "Manufacturer-Approved" },
                      { icon: BadgeCheck, label: "OEM-Spec Oils & Filters" },
                      { icon: Star, label: "5.0 Rated" },
                    ].map((bdg) => (
                      <span key={bdg.label} className="inline-flex items-center gap-1.5 rounded-full border border-ink-soft bg-ink-surface px-3 py-1.5 text-xs text-white/70">
                        <bdg.icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.75} />
                        {bdg.label}
                      </span>
                    ))}
                  </div>
                  <p className="text-center text-xs text-white/35">We'll never share your details. Required fields marked *.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
